'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface SearchBarProps {
  onSearch: (query: string) => void;
  className?: string;
}

export default function SearchBar({
  onSearch,
  className = '',
}: SearchBarProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [suggestions, setSuggestions] = useState<string[]>([]);

  const commonSearches = [
    'How to activate my card',
    'Privacy and data protection',
    'Bulk order for society',
    'Payment methods',
    'Card not working',
    'Change phone number',
    'Refund policy',
    'Installation guide',
  ];

  const handleInputChange = (value: string) => {
    setSearchQuery(value);

    if (value.length > 2) {
      const filtered = commonSearches.filter((search) =>
        search.toLowerCase().includes(value.toLowerCase())
      );
      setSuggestions(filtered);
    } else {
      setSuggestions([]);
    }
  };

  const handleSearch = (query: string) => {
    setSearchQuery(query);
    setSuggestions([]);
    onSearch(query);
  };

  return (
    <div className={`relative ${className}`}>
      <div className="relative">
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => handleInputChange(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleSearch(searchQuery)}
          placeholder="Search for help articles, guides, or FAQs..."
          className="w-full px-6 py-4 pr-12 bg-white text-gray-900 border-2 border-gray-200 rounded-lg focus:outline-none focus:border-[#42AF47] transition"
          aria-label="Search support articles"
        />

        <button
          onClick={() => handleSearch(searchQuery)}
          className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-[#42AF47] transition"
          aria-label="Submit search"
        >
          <Icon name="MagnifyingGlassIcon" size={24} />
        </button>
      </div>

      {suggestions.length > 0 && (
        <div className="absolute z-10 w-full mt-2 bg-white border border-gray-200 rounded-lg shadow-md">
          {suggestions.map((suggestion, index) => (
            <button
              key={index}
              onClick={() => handleSearch(suggestion)}
              className="w-full px-6 py-3 text-left text-gray-700 hover:bg-gray-50 transition first:rounded-t-lg last:rounded-b-lg"
            >
              <div className="flex items-center gap-3">
                <Icon
                  name="MagnifyingGlassIcon"
                  size={16}
                  className="text-gray-400"
                />
                <span>{suggestion}</span>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
